import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  ArrowDown,
  ArrowLeft,
  ArrowSquareOut,
  At,
  EyeSlash,
  Gavel,
  Key,
  SealCheck,
  TrashSimple,
  X,
} from "@phosphor-icons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  LoadingOverlay,
  initialsForMember,
  useToast,
} from "@breakingthelines/design-system";

import type { AdminUser, MembershipDTO } from "@/lib/admin-api";
import {
  banUser,
  cascadeHideUser,
  setUserVerified,
  getUserPlatformMembership,
  listUsers,
  revokeMembership,
  setMembershipExpiry,
  makeMembershipPermanent,
  resetHandle,
  unbanUser,
} from "@/lib/admin-api";
import { membershipExpiryLabel } from "@/lib/scholarship";

import { GrantScholarshipSheet } from "./grant-scholarship-sheet";
import { MembershipExpirySheet } from "./membership-expiry-sheet";
import styles from "./user-detail.module.css";

export function UserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [banLoading, setBanLoading] = useState(false);
  const [resetHandleLoading, setResetHandleLoading] = useState(false);
  const [banError, setBanError] = useState<string | null>(null);
  const [cascadeAction, setCascadeAction] = useState<"hide" | "derank" | null>(null);
  const [cascadeNotice, setCascadeNotice] = useState<string | null>(null);
  const [grantSheetOpen, setGrantSheetOpen] = useState(false);

  const [membership, setMembership] = useState<MembershipDTO | null>(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const [membershipReloadKey, setMembershipReloadKey] = useState(0);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [extendDateInput, setExtendDateInput] = useState("");
  const [extending, setExtending] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    listUsers({ query: id, pageSize: 5 })
      .then((data) => {
        if (cancelled) return;
        const normalized = id.toLowerCase();
        const match =
          data.users.find((item) => item.username?.toLowerCase() === normalized) ??
          data.users.find((item) => item.displayName?.toLowerCase() === normalized) ??
          data.users[0];
        if (!match) {
          setError("User not found.");
          setUser(null);
          return;
        }
        setUser(match);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message ?? "Failed to load user.");
        setUser(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Runs once the user is resolved (needs the real user id, not the route's
  // handle/id ambiguity) and again whenever membershipReloadKey changes —
  // the single refetch path every mutation below (grant/extend/revoke) uses
  // to make sure the card reflects the server's post-mutation state rather
  // than trusting each mutation's own response shape.
  useEffect(() => {
    if (!user?.id) {
      setMembership(null);
      return;
    }
    let cancelled = false;
    setMembershipLoading(true);
    setMembershipError(null);

    getUserPlatformMembership(user.id)
      .then((data) => {
        if (cancelled) return;
        setMembership(data.found ? data.membership : null);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setMembershipError(err.message ?? "Failed to load membership");
        setMembership(null);
      })
      .finally(() => {
        if (!cancelled) setMembershipLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id, membershipReloadKey]);

  const isBanned = Boolean(user?.bannedAt);

  function closeBanDialog() {
    setBanDialogOpen(false);
    setBanReason("");
    setBanError(null);
  }

  async function handleBan() {
    if (!user) return;
    setBanLoading(true);
    setBanError(null);
    try {
      await banUser({ userId: user.id, reason: banReason.trim() });
      setUser({ ...user, bannedAt: new Date().toISOString(), banReason: banReason.trim() });
      closeBanDialog();
    } catch (err: unknown) {
      setBanError(err instanceof Error ? err.message : "Failed to ban user");
    } finally {
      setBanLoading(false);
    }
  }

  async function handleUnban() {
    if (!user || !window.confirm(`Are you sure you want to unban ${user.username ?? user.id}?`))
      return;
    setBanLoading(true);
    try {
      await unbanUser({ userId: user.id });
      setUser({ ...user, bannedAt: null, banReason: undefined });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to unban user");
    } finally {
      setBanLoading(false);
    }
  }

  async function handleResetHandle() {
    if (!user?.username) return;
    if (
      !window.confirm(
        `Take the handle @${user.username} away from this account? They will be moved to a random placeholder (user_...), the handle is freed, and /@${user.username} will stop resolving rather than redirecting to their new one. Their content and account are untouched.`,
      )
    ) {
      return;
    }
    setResetHandleLoading(true);
    setError(null);
    try {
      await resetHandle({ userId: user.id, handle: user.username });
      // The new placeholder is minted server-side, so re-fetch rather than
      // guessing it — showing a stale @handle here would be worse than a reload.
      window.location.reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to reset handle");
    } finally {
      setResetHandleLoading(false);
    }
  }

  async function handleCascadeHide() {
    if (
      !user ||
      !window.confirm(
        `Hide ALL content and thoughts by ${user.username ?? user.id}? This removes every item they have authored from public/discovery surfaces (feeds, search, trending) — it stays visible on their own profile and direct links still work. This does not remove/404 anything; use Remove per-item for that.`,
      )
    ) {
      return;
    }
    setCascadeAction("hide");
    setCascadeNotice(null);
    try {
      const result = await cascadeHideUser(user.id, "MODERATION_ACTION_HIDE");
      setCascadeNotice(
        `Hidden ${result.contentAffected ?? 0} content item(s) and ${result.thoughtsAffected ?? 0} thought(s) from public surfaces (still visible on their profile).`,
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to hide user content");
    } finally {
      setCascadeAction(null);
    }
  }

  /**
   * Toggle the verification badge.
   *
   * Verification is an editorial statement that this account is a genuine
   * author, and it is what makes their articles AND thoughts indexable — it is
   * not the authorized-publisher allowlist, which records a commercial partner
   * arrangement. Un-verifying therefore takes an account's content out of the
   * index, so it confirms first.
   */
  async function handleToggleVerified() {
    if (!user) return;
    const next = !user.isVerified;
    if (
      !next &&
      !window.confirm(
        `Remove verification from ${user.username ? `@${user.username}` : user.id}? ` +
          `Their articles and thoughts will stop being indexed by search engines.`,
      )
    ) {
      return;
    }
    setVerifyLoading(true);
    try {
      const res = await setUserVerified(user.id, next);
      // Trust the response, not the optimistic guess: it reports the state after
      // the call, so a no-op cannot leave the button showing something untrue.
      setUser((prev) => (prev ? { ...prev, isVerified: res.verified } : prev));
      toast.success(
        res.verified ? "Their content is now indexable." : "Their content is no longer indexed.",
        res.verified ? "Verified" : "Verification removed",
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update verification",
        "Couldn't update verification",
      );
    } finally {
      setVerifyLoading(false);
    }
  }

  async function handleCascadeDerank() {
    if (
      !user ||
      !window.confirm(
        `Derank ALL content and thoughts by ${user.username ?? user.id}? This applies a reach penalty across everything they have authored, evicting it from public/discovery surfaces (feeds, search, trending) — it stays visible on their own profile and direct links still work. This is a softer signal than Hide, not a stronger "hidden" state; use Hide for that, or Remove per-item to 404 an item entirely.`,
      )
    ) {
      return;
    }
    setCascadeAction("derank");
    setCascadeNotice(null);
    try {
      const result = await cascadeHideUser(user.id, "MODERATION_ACTION_DERANK");
      setCascadeNotice(
        `Deranked ${result.contentAffected ?? 0} content item(s) and ${result.thoughtsAffected ?? 0} thought(s) from public surfaces (still visible on their profile).`,
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to derank user content");
    } finally {
      setCascadeAction(null);
    }
  }

  function closeExtendDialog() {
    setExtendDialogOpen(false);
    setExtendDateInput("");
  }

  async function handleExtendMembership() {
    if (!membership || !extendDateInput) return;
    setExtending(true);
    try {
      await setMembershipExpiry(membership.id, `${extendDateInput}T00:00:00.000Z`);
      toast.success(`Access now ends ${formatDate(extendDateInput)}.`, "End date updated");
      closeExtendDialog();
      setMembershipReloadKey((v) => v + 1);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update the end date",
        "Couldn't update end date",
      );
    } finally {
      setExtending(false);
    }
  }

  async function handleMakePermanent() {
    if (!membership) return;
    setExtending(true);
    try {
      await makeMembershipPermanent(membership.id);
      toast.success("Access now never expires.", "Made permanent");
      closeExtendDialog();
      setMembershipReloadKey((v) => v + 1);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to make the membership permanent",
        "Couldn't make permanent",
      );
    } finally {
      setExtending(false);
    }
  }

  async function handleRevokeMembership() {
    if (!membership) return;
    setRevoking(true);
    try {
      await revokeMembership(membership.id);
      toast.success("Access revoked.", "Revoked");
      setRevokeDialogOpen(false);
      setMembershipReloadKey((v) => v + 1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to revoke access", "Revoke failed");
    } finally {
      setRevoking(false);
    }
  }

  const displayName = useMemo(
    () => user?.displayName || user?.username || id || "Unknown User",
    [id, user],
  );
  const handle = useMemo(
    () => (user?.username ? `@${user.username}` : id ? `@${id}` : "@unknown"),
    [id, user],
  );
  const tierLabel = useMemo(() => formatTier(user?.tier), [user?.tier]);
  const bio = useMemo(() => (user?.bio?.trim() ? user.bio : "No bio available."), [user?.bio]);
  const since = useMemo(() => formatDate(user?.createdAt), [user?.createdAt]);

  return (
    <div className={styles.page}>
      <div className={styles.contentCard}>
        <button type="button" className={styles.backRow} onClick={() => navigate(-1)}>
          <ArrowLeft size={14} />
          Go back
        </button>

        {loading ? <LoadingOverlay size={24} radius="md" label="Loading user..." /> : null}
        {!loading && error ? <div className={styles.errorState}>{error}</div> : null}
        {cascadeNotice ? <div className={styles.errorState}>{cascadeNotice}</div> : null}
        <section className={styles.headerCard}>
          <div className={styles.profileRow}>
            <div className={styles.profileInfo}>
              <Avatar size="xxl" className={`size-[250px] ${styles.avatar}`} aria-hidden="true">
                <AvatarFallback>{initialsForMember(displayName)}</AvatarFallback>
              </Avatar>
              <div className={styles.names}>
                <h2>{displayName}</h2>
                <p>{handle}</p>
              </div>
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                className={`${styles.actionButton} ${isBanned ? styles.danger : ""}`}
                disabled={banLoading}
                onClick={() => (isBanned ? void handleUnban() : setBanDialogOpen(true))}
              >
                <Gavel size={16} />{" "}
                {banLoading ? (isBanned ? "Unbanning…" : "Banning…") : isBanned ? "Unban" : "Ban"}
              </button>
              <button
                type="button"
                className={styles.actionButton}
                disabled={resetHandleLoading || !user?.username}
                onClick={() => void handleResetHandle()}
                title="Take this handle away and move the account to a random placeholder"
              >
                <At size={16} /> {resetHandleLoading ? "Resetting…" : "Reset handle"}
              </button>
              <button
                type="button"
                className={styles.actionButton}
                disabled={verifyLoading || !user}
                onClick={() => void handleToggleVerified()}
                title={
                  user?.isVerified
                    ? "Remove the verification badge — their content stops being indexed"
                    : "Mark this account as a genuine author — makes their content indexable"
                }
              >
                <SealCheck size={16} />{" "}
                {verifyLoading
                  ? user?.isVerified
                    ? "Removing…"
                    : "Verifying…"
                  : user?.isVerified
                    ? "Unverify"
                    : "Verify"}
              </button>
              <button type="button" className={styles.actionButton}>
                <Key size={16} /> Password Reset
              </button>
              <button
                type="button"
                className={`${styles.actionButton} ${styles.danger}`}
                disabled={cascadeAction !== null}
                onClick={() => void handleCascadeHide()}
              >
                <EyeSlash size={16} />{" "}
                {cascadeAction === "hide" ? "Hiding…" : "Hide all by this user"}
              </button>
              <button
                type="button"
                className={`${styles.actionButton} ${styles.danger}`}
                disabled={cascadeAction !== null}
                onClick={() => void handleCascadeDerank()}
              >
                <ArrowDown size={16} />{" "}
                {cascadeAction === "derank" ? "Deranking…" : "Derank all by this user"}
              </button>
              <button type="button" className={`${styles.actionButton} ${styles.danger}`}>
                <TrashSimple size={16} /> Delete Account
              </button>
            </div>
          </div>
          <div className={styles.sectionCard}>
            <p className={styles.sectionTitle}>Bio</p>
            <p className={styles.sectionCopy}>{bio}</p>
          </div>
        </section>

        <section className={styles.contentGrid}>
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionHeading}>
              <p className={styles.sectionTitle}>User Content</p>
              <p className={styles.sectionCopy}>
                Manage who is member of the “Name of the Squad” squad.
              </p>
            </div>
            <div className={styles.sectionCard}>
              <div className={styles.linkStack}>
                <div className={styles.linkCard}>
                  Articles <ArrowSquareOut size={14} />
                </div>
                <div className={styles.linkCard}>
                  Thoughts <ArrowSquareOut size={14} />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.sectionWrapper}>
            <div className={styles.sectionHeading}>
              <p className={styles.sectionTitle}>Subscription</p>
              <p className={styles.sectionCopy}>Grant a BTL platform membership.</p>
            </div>
            <div className={`${styles.sectionCard} ${styles.subscriptionCard}`}>
              <div className={styles.subscriptionIcon} aria-hidden />
              <div className={styles.subscriptionFooter}>
                <span className={styles.subscriptionPlan}>
                  {tierLabel}
                  {membership?.isComp ? (
                    <Badge variant="tintedWarn" className={styles.scholarshipBadge}>
                      Scholarship
                    </Badge>
                  ) : null}
                </span>
                <div className={styles.subscriptionMetaGroup}>
                  <div className={styles.subscriptionMeta}>
                    <span>Since</span>
                    <strong>{since}</strong>
                  </div>
                  {membership ? (
                    <div className={styles.subscriptionMeta}>
                      <span>Access</span>
                      <strong>{membershipExpiryLabel(membership)}</strong>
                    </div>
                  ) : membershipLoading ? (
                    <div className={styles.subscriptionMeta}>
                      <span>Access</span>
                      <strong>…</strong>
                    </div>
                  ) : null}
                </div>
              </div>
              {membershipError ? (
                <p className={styles.subscriptionError} role="alert">
                  {membershipError}
                </p>
              ) : null}
              {membership?.isComp ? (
                <div className={styles.subscriptionActions}>
                  <button
                    type="button"
                    className={styles.subscriptionActionButton}
                    onClick={() => {
                      setExtendDateInput("");
                      setExtendDialogOpen(true);
                    }}
                  >
                    Extend / change end date
                  </button>
                  <button
                    type="button"
                    className={`${styles.subscriptionActionButton} ${styles.subscriptionActionButtonDanger}`}
                    onClick={() => setRevokeDialogOpen(true)}
                  >
                    Revoke
                  </button>
                </div>
              ) : null}
              {/* Grant is only offered when the user has no active platform
                  membership. Granting on top of one is either redundant (a
                  scholarship is already active -> use Extend/Revoke above) or a
                  server error (GrantMembership rejects a duplicate with
                  ErrAlreadySubscribed, including for a paid member). */}
              {!membershipLoading && !membership ? (
                <button
                  type="button"
                  className={styles.grantScholarshipButton}
                  onClick={() => setGrantSheetOpen(true)}
                  disabled={!user}
                >
                  Grant scholarship
                </button>
              ) : null}
            </div>
          </div>

          <div className={styles.sectionWrapper}>
            <div className={styles.sectionHeading}>
              <p className={styles.sectionTitle}>Moderation History</p>
              <p className={styles.sectionCopy}>
                Manage who is member of the “Name of the Squad” squad.
              </p>
            </div>
            <div className={`${styles.sectionCard} ${styles.historyEmpty}`}>No History so far</div>
          </div>
        </section>
      </div>

      {banDialogOpen ? (
        <div className={styles.dialogOverlay} onMouseDown={() => closeBanDialog()}>
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="ban-user-title"
            className={styles.dialogContent}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className={styles.dialogHeader}>
              <div>
                <h2 id="ban-user-title" className={styles.dialogTitle}>
                  Ban User
                </h2>
                <p className={styles.dialogDescription}>
                  Ban <strong>{user?.username ?? user?.id}</strong> from the platform. They will
                  lose access immediately.
                </p>
              </div>
              <button
                type="button"
                className={styles.dialogClose}
                onClick={() => closeBanDialog()}
                aria-label="Close"
                disabled={banLoading}
              >
                <X size={14} weight="bold" />
              </button>
            </header>

            <div className={styles.dialogBody}>
              <form
                className={styles.dialogForm}
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleBan();
                }}
              >
                <label htmlFor="ban-reason" className={styles.dialogLabel}>
                  Reason
                </label>
                <textarea
                  id="ban-reason"
                  value={banReason}
                  onChange={(event) => setBanReason(event.currentTarget.value)}
                  className={styles.dialogTextarea}
                  placeholder="Why is this user being banned?"
                  rows={3}
                />
                {banError ? (
                  <p className={styles.dialogError} role="alert">
                    {banError}
                  </p>
                ) : null}
              </form>
            </div>

            <footer className={styles.dialogFooter}>
              <Button
                type="button"
                variant="secondary"
                disabled={banLoading}
                onClick={() => closeBanDialog()}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={banLoading || banReason.trim().length === 0}
                onClick={() => void handleBan()}
              >
                {banLoading ? "Banning…" : "Ban User"}
              </Button>
            </footer>
          </section>
        </div>
      ) : null}

      {user ? (
        <GrantScholarshipSheet
          open={grantSheetOpen}
          onClose={() => setGrantSheetOpen(false)}
          subscriber={{ id: user.id, username: user.username, displayName: user.displayName }}
          onGranted={() => setMembershipReloadKey((v) => v + 1)}
        />
      ) : null}

      <MembershipExpirySheet
        open={extendDialogOpen}
        onClose={closeExtendDialog}
        title="Extend / change end date"
        description={
          membership
            ? `Choose a new date for when ${displayName}'s access ends. It currently ${
                membership.expiresAt ? `ends ${formatDate(membership.expiresAt)}` : "never expires"
              }.`
            : ""
        }
        dateValue={extendDateInput}
        onDateChange={setExtendDateInput}
        onSave={() => void handleExtendMembership()}
        saving={extending}
        saveLabel="Save new date"
        onMakePermanent={membership?.expiresAt ? () => void handleMakePermanent() : undefined}
      />

      {/*
       * Kept as an AlertDialog (rather than converted to a Sheet like the
       * Extend dialog above): this is a pure destructive confirmation, not a
       * form, and Base UI's AlertDialogRoot hardcodes
       * `disablePointerDismissal: true` with no prop to override it — an
       * intentional a11y choice for confirmations (accidental outside-click
       * shouldn't revoke someone's access). Escape still closes it.
       */}
      <AlertDialog
        open={revokeDialogOpen}
        onOpenChange={(open) => !open && setRevokeDialogOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke access</AlertDialogTitle>
            <AlertDialogDescription>
              This ends {displayName}&apos;s access immediately. This cannot be undone from here;
              granting it again is a new grant.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={revoking}
              onClick={() => void handleRevokeMembership()}
            >
              {revoking ? "Revoking…" : "Revoke access"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function formatTier(tier?: string) {
  switch (tier) {
    case "USER_TIER_FREE":
      return "Free";
    case "USER_TIER_PRO":
      return "Pro";
    case "USER_TIER_LINE_BREAKERS":
      return "Line Breakers";
    default:
      return "Free";
  }
}

function formatDate(value?: string) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
}
